import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Store } from '@ngrx/store';
import { distinctUntilChanged, filter, Observable, Subject, takeUntil } from 'rxjs';
import { AppState } from 'src/app/core/app.state';
import { IAgent } from 'src/app/features/agents/models/agents.models';
import { ICommissionNegotiation, ICommissionThreshold, INewCommissionDTO } from 'src/app/features/agents/models/commissions.models';
import { CommissionService } from 'src/app/features/agents/services/commission.service';

@Component({
  selector: 'app-new-commission-summary',
  templateUrl: './new-commission-summary.component.html',
  styleUrls: ['./new-commission-summary.component.scss'],
})
export class NewCommissionSummaryComponent implements OnInit {
  @Input() commission!: INewCommissionDTO;
  @Output() nextStep = new EventEmitter<Partial<INewCommissionDTO>>();
  @Output() previousStep = new EventEmitter<Partial<INewCommissionDTO>>();

  orderValue: FormControl = new FormControl(500);
  orderExtraDiscount: FormControl = new FormControl(0);

  agents$: Observable<IAgent[]> = this.store.select((state: AppState) => state.agents.agents);
  agents: IAgent[] = [];
  filteredAgents: IAgent[] = [];
  selectedAgents: number[] = [];
  searchInput = new FormControl('');

  destroy$ = new Subject<void>();

  calculationTypeDesc: string = this.commissionService.getDefaultCommissionCalculationTypeDesc();

  constructor(private store: Store<AppState>, private commissionService: CommissionService) {}

  ngOnInit(): void {
    if(this.commission?.calculation_method) {
      this.calculationTypeDesc = this.commissionService.getCommissionCalculationTypeDesc(this.commission.calculation_method);
    }

    this.agents$
      .pipe(
        filter((agents) => agents.length >= 0),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((agents: IAgent[]) => {
        this.agents = agents;
        this.filteredAgents = [...agents];
      });
  }

  get FinalOrderValue() {
    const value = this.orderValue.getRawValue();
    const extraDiscount = this.orderExtraDiscount.getRawValue();
    return value ? value - value * (extraDiscount / 100) : 0;
  }

  get CommissionValue() {
    const finalOrderValue = this.FinalOrderValue;
    const commissionDiscount = this.AppliedRule?.commission;
    if (!commissionDiscount) {
      return 0;
    }

    return finalOrderValue * (commissionDiscount / 100);
  }

  get AppliedRule()  {
    const { commission, thresholds, negotiations } = this.commission
    const value = this.orderValue.getRawValue();
    const extraDiscount = this.orderExtraDiscount.getRawValue();
    // Check If Negotiation Rule Applied
    const negotiationFound = negotiations?.find((negotiation: ICommissionNegotiation) => extraDiscount >= negotiation.min_extra_discount && (extraDiscount <= negotiation.max_extra_discount || negotiation.max_extra_discount == null)) || null;
    const thresholdFound = thresholds?.find((threshold: ICommissionThreshold) => value >= threshold.min_order_value && value <= threshold.max_order_value || threshold.max_order_value == null) || null;
    
    if(negotiationFound) {
      return { type: 'negotiation', commission: negotiationFound.new_commission };
    }

    // Check If Threshold Rule Applied
    if(thresholdFound) {
      return { type: 'threshold', commission: thresholdFound.new_commission };
    }

    if (commission!) {
      return { type: 'base', commission: commission };
    }

    return null;
  }

  /**
   * Returns the values needed for the info box:
   * - maxCommission: the highest new_commission percentage (from thresholds, negotiations, or base)
   * - maxCommissionType: where the max commission comes from ('threshold', 'negotiation', 'base')
   * - maxCommissionContext: the context (order value, extra discount range, or null for base)
   * - minCommission: the lowest new_commission percentage (from thresholds, negotiations, or base)
   * - minCommissionType: where the min commission comes from ('threshold', 'negotiation', 'base')
   * - minCommissionContext: the context (order value, extra discount range, or null for base)
   */
  getCommissionInfoBoxValues(): {
    maxCommission: number | null,
    maxCommissionType: 'threshold' | 'negotiation' | 'base' | null,
    maxCommissionContext: any,
    minCommission: number | null,
    minCommissionType: 'threshold' | 'negotiation' | 'base' | null,
    minCommissionContext: any
  } {
    const thresholds = this.commission.thresholds;
    const negotiations = this.commission.negotiations;
    const baseCommission = typeof this.commission?.commission === 'number' ? this.commission.commission : null;

    // Collect all commissions with their context
    const allCommissions: Array<{
      value: number,
      type: 'threshold' | 'negotiation' | 'base',
      context: any
    }> = [];

    thresholds.forEach((t: ICommissionThreshold) => {
      if (typeof t.new_commission === 'number') {
        allCommissions.push({ value: t.new_commission, type: 'threshold', context: { minOrderValue: t.min_order_value, maxOrderValue: t.max_order_value } });
      }
    });
    
    negotiations.forEach((n: ICommissionNegotiation) => {
      if (typeof n.new_commission === 'number') {
        allCommissions.push({ value: n.new_commission, type: 'negotiation', context: { minExtraDiscount: n.min_extra_discount, maxExtraDiscount: n.max_extra_discount } });
      }
    });

    if (baseCommission) {
      allCommissions.push({ value: baseCommission, type: 'base', context: null });
    }

    if (allCommissions.length === 0) {
      return {
        maxCommission: null,
        maxCommissionType: null,
        maxCommissionContext: null,
        minCommission: null,
        minCommissionType: null,
        minCommissionContext: null
      };
    }

    // Find max and min
    let max = allCommissions[0];
    let min = allCommissions[0];
    allCommissions.forEach((c) => {
      if (c.value > max.value) max = c;
      if (c.value < min.value) min = c;
    });

    return {
      maxCommission: max.value,
      maxCommissionType: max.type,
      maxCommissionContext: max.context,
      minCommission: min.value,
      minCommissionType: min.type,
      minCommissionContext: min.context
    };
  }

  search(event: any) {
    const key = event.target.value.toLowerCase();

    if (key != '') {
      this.filteredAgents = this.agents.filter((agent) => `${agent.first_name} ${agent.last_name}`.toLowerCase().includes(key));
    } else {
      this.filteredAgents = this.agents;
    }
  }


  isAllAgentsSelected(): boolean {
    const allAgentIds = this.filteredAgents.map((agent: IAgent) => agent.id);
    return allAgentIds.length ? allAgentIds.every((id: any) => this.selectedAgents.includes(id)) : false;
  }

  isPartialSelectedAll(): boolean {
    const allAgentIds = this.filteredAgents.map((agent: IAgent) => agent.id);
    const selectedCount = allAgentIds.filter((id: any) => this.selectedAgents.includes(id)).length;
    return selectedCount > 0 && selectedCount < allAgentIds.length;
  }

  toggleAllAgents(checked: boolean) {
    const allIds = this.filteredAgents.map((l: IAgent) => l.id!);
    this.selectedAgents = checked ? [...allIds] : [];
  }

  toggleAgent(agentId: number, checked: boolean) {
    if (checked) {
      this.selectedAgents.push(agentId);
    } else {
      this.selectedAgents = this.selectedAgents.filter((id) => id !== agentId);
    }
  }

  trackByAgentId(_index: number, agent: IAgent): string | number {
    return agent.id!;
  }
  create() {
    this.nextStep.emit({agent_ids: this.selectedAgents});
  }

  previous() {
    this.previousStep.emit({});
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
